import milestonesData from '../data/collection/milestones.json';
import { itemRegistry } from '../registries/ItemRegistry.js';

/**
 * CollectionStateManager handles persistent collection tracking.
 *
 * Collection data survives ALL prestiges - it tracks items found
 * across the player's entire history with the game.
 *
 * Provides a focused API for collection management while keeping
 * actual state in GameState.state.collection for save compatibility.
 */
export class CollectionStateManager {
    /**
     * @param {GameState} gameState - Reference to main game state
     */
    constructor(gameState) {
        this.gameState = gameState;
        this.milestones = milestonesData.milestones;
    }

    /**
     * Get the collection state object (shorthand)
     * @private
     */
    get collection() {
        return this.gameState.state.collection;
    }

    // ===== Read Operations =====

    /**
     * Get set of all found item baseIds
     * @returns {Set<string>} Set of baseId strings
     */
    getFoundItems() {
        return new Set(this.collection?.found || []);
    }

    /**
     * Get count of unique items found
     * @returns {number} Number of unique items found
     */
    getFoundCount() {
        return this.collection?.found?.length || 0;
    }

    /**
     * Check if a specific item has been found
     * @param {string} baseId - The item's base ID
     * @returns {boolean} True if item has been found
     */
    hasFoundItem(baseId) {
        return this.collection?.found?.includes(baseId) || false;
    }

    /**
     * Get mastery count for an item (times found)
     * @param {string} baseId - The item's base ID
     * @returns {number} Number of times this item has been found
     */
    getMastery(baseId) {
        return this.collection?.mastery?.[baseId] || 0;
    }

    /**
     * Get total items available in the game
     * @returns {number} Total item count (50)
     */
    getTotalItems() {
        return itemRegistry.size;
    }

    /**
     * Get the next unclaimed milestone
     * @returns {Object|null} Next milestone object or null if all claimed
     */
    getNextMilestone() {
        const claimedThresholds = this.getClaimedMilestones();
        const foundCount = this.getFoundCount();

        // Find first milestone that is both reachable and unclaimed
        for (const milestone of this.milestones) {
            if (!claimedThresholds.includes(milestone.threshold) && foundCount >= milestone.threshold) {
                return milestone;
            }
        }

        // Find next milestone to work toward (unreached but unclaimed)
        for (const milestone of this.milestones) {
            if (!claimedThresholds.includes(milestone.threshold)) {
                return milestone;
            }
        }

        return null;
    }

    /**
     * Get array of claimed milestone thresholds
     * @returns {number[]} Array of threshold values already claimed
     */
    getClaimedMilestones() {
        return this.collection?.claimedMilestones || [];
    }

    /**
     * Get all active stat bonuses from claimed milestones
     * @returns {Object} Object mapping stat names to bonus values
     */
    getMilestoneRewards() {
        const claimedThresholds = this.getClaimedMilestones();
        const bonuses = {};

        for (const milestone of this.milestones) {
            if (claimedThresholds.includes(milestone.threshold) && milestone.reward_type === 'stat_bonus') {
                // Support both new format (rewards array) and legacy format (reward object)
                const rewards = milestone.rewards || (milestone.reward ? [milestone.reward] : []);
                for (const reward of rewards) {
                    bonuses[reward.stat] = (bonuses[reward.stat] || 0) + reward.value;
                }
            }
        }

        return bonuses;
    }

    /**
     * Get all milestone definitions
     * @returns {Array} Array of milestone objects
     */
    getAllMilestones() {
        return this.milestones;
    }

    // ===== Write Operations =====

    /**
     * Record that an item was found (idempotent for collection)
     * @param {string} baseId - The item's base ID
     * @returns {boolean} True if this was a NEW discovery, false if already found
     */
    recordItemFound(baseId) {
        if (!this.collection) return false;

        // Initialize found array if needed
        if (!this.collection.found) {
            this.collection.found = [];
        }

        // Check if already found (idempotent)
        if (this.collection.found.includes(baseId)) {
            return false;
        }

        // Add to found items
        this.collection.found.push(baseId);
        this.gameState.save();
        return true;
    }

    /**
     * Increment mastery count for an item
     * @param {string} baseId - The item's base ID
     */
    incrementMastery(baseId) {
        if (!this.collection) return;

        // Initialize mastery object if needed
        if (!this.collection.mastery) {
            this.collection.mastery = {};
        }

        this.collection.mastery[baseId] = (this.collection.mastery[baseId] || 0) + 1;
        this.gameState.save();
    }

    /**
     * Claim a milestone reward (prevents double-claiming)
     * @param {number} threshold - The milestone threshold to claim
     * @returns {Object|false} The milestone object if claimed, false if already claimed or invalid
     */
    claimMilestone(threshold) {
        if (!this.collection) return false;

        // Initialize claimedMilestones array if needed
        if (!this.collection.claimedMilestones) {
            this.collection.claimedMilestones = [];
        }

        // Check if already claimed
        if (this.collection.claimedMilestones.includes(threshold)) {
            return false;
        }

        // Find the milestone
        const milestone = this.milestones.find(m => m.threshold === threshold);
        if (!milestone) {
            return false;
        }

        // Check if player has reached the threshold
        if (this.getFoundCount() < threshold) {
            return false;
        }

        // Claim it
        this.collection.claimedMilestones.push(threshold);
        this.gameState.save();

        // Invalidate StatCalculator cache so new bonuses take effect immediately
        if (this.gameState.statCalculator) {
            this.gameState.statCalculator.invalidateAll();
        }

        return milestone;
    }
}
